import { getCurrentUser } from "api/auth.service";
import { JwtAuthData } from "types/interface.types";
import { Cart, OverdueDebtor } from "types/model.types";
import { OrderStage, ShoppingCart, getStorageKey } from "./constants";

export const getSeverity = (debtor: OverdueDebtor): number => {
  if (debtor.month3 > 0) return 3;
  if (debtor.month2 > 0) return 2;
  if (debtor.month1 > 0) return 1;
  return 0;
};
export const transformRef = (ref: string | null): string => {
  if (ref === null) return "";
  if (!ref.includes("/")) return ref;

  const [invoice, batch] = ref.split("/");
  return `${batch}/${invoice}`;
};

export const formatToPrecision = (value: number, precision = 2): number => {
  return parseFloat(value.toFixed(precision));
};
export const calculateSalesMetrics = (row: any) => {
  const now = new Date();
  const daysInMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
  ).getDate();
  const prevMonth = now.getMonth(); // 0-based

  let avgCurrentMonths = prevMonth + now.getDate() / daysInMonth;
  avgCurrentMonths = formatToPrecision(avgCurrentMonths, 2);

  let units = row.unitsCtn ?? 0;
  if (units <= 0) units = 1;

  const avg = formatToPrecision((row.total ?? 0) / units / avgCurrentMonths, 2);
  const exAvg = formatToPrecision((row.lastSale ?? 0) / units / 12, 2);

  const denom =
    row.total == null || row.total === 0
      ? avgCurrentMonths
      : row.total / units / avgCurrentMonths;

  const months = formatToPrecision(
    ((row.sord ?? 0) / units + (row.onHand ?? 0) / units) / denom,
    2,
  );
  const ams = formatToPrecision((row.total ?? 0) / 24 / avgCurrentMonths, 2);

  return { avg, exAvg, ams, months };
};

export const parseNZDate = (value: string | null): Date | null => {
  if (!value) return null;

  // Expect dd/mm/yyyy
  const parts = value.split("/");
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts.map(Number);

  if (!dd || !mm || !yyyy) return null;

  // JS Date uses 0‑based month
  //const d = new Date(yyyy, mm - 1, dd);
  const d = new Date(Date.UTC(yyyy, mm - 1, dd));

  // Validate that the date is real (e.g., 31/02/2024 should fail)
  if (
    d.getFullYear() !== yyyy ||
    d.getMonth() !== mm - 1 ||
    d.getDate() !== dd
  ) {
    return null;
  }

  return d;
};

export const parseNumberOrNull = (value: string | null): number | null => {
  if (!value) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const truncate = (text: string, text2 = "", max = 100) => {
  const combined = `${text} ${text2}`;
  return combined.length > max ? combined.slice(0, max) + "..." : combined;
};
export function getLocalDateString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export const safeUnits = (u?: number | null) => (u && u > 0 ? u : 1);
/**
 * Safely log a response object, redacting sensitive data
 * @param label the label to log
 * @param response the response object to log
 */
export const safeLog = (label: string, data?: any) => {
  if (process.env.NODE_ENV == "production") return;

  if (!data) {
    console.warn(`${label}: No response to log`);
    return;
  }

  const SENSITIVE_KEYS = [
    "password",
    "token",
    "accessToken",
    "email",
    "config.data",
    "role",
    "xsrfCookieName",
    "xsrfHeaderName",
    "userId",
    "user",
    "transitional",
    "withCredentials",
    "headers",
    "auth",
    "baseURL",
    "url",
    "data",
    "pwd",
    "method",
    "params",
    "transformRequest",
  ];

  // Recursively sanitize sensitive fields
  const sanitize = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(sanitize);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => {
          if (SENSITIVE_KEYS.includes(key)) {
            return [key, "[REDACTED]"];
          }
          return [key, sanitize(value)];
        }),
      );
    }
    return obj;
  };
  const cleaned = sanitize(data);
  // 👉 If array, use JSON.stringify for better visibility
  const output = Array.isArray(cleaned)
    ? JSON.stringify(cleaned, null, 2)
    : JSON.stringify(cleaned);

  console.info(`${label}: ${output}`);
};
export const normalizeString = (input?: string): string => {
  return (input ?? "").trim().toLowerCase();
};
export const getPaginatedData = <T>(
  items: T[],
  page: number,
  pageSize: number,
): T[] => {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return items.slice(startIndex, endIndex);
};
/**
 * Client side pagination
 * @param items
 * @param activePage
 * @param itemsPerPage
 * @returns
 */
export const paginator = (
  items: any,
  activePage: number,
  itemsPerPage: number,
) => {
  const page = activePage || 1,
    per_page = itemsPerPage || 10,
    offset = (page - 1) * per_page,
    paginatedItems = items.slice(offset).slice(0, per_page),
    total_pages = Math.ceil(items.length / per_page);

  return {
    page: page,
    per_page: per_page,
    pre_page: page - 1 ? page - 1 : null,
    next_page: total_pages > page ? page + 1 : null,
    total: items.length,
    total_pages: total_pages,
    data: paginatedItems,
  };
};
export const saveCartToLocalStorage = (cart: Cart) => {
  localStorage.setItem(`${getStorageKey(ShoppingCart)}`, JSON.stringify(cart));
};
export const getCartFromLocalStorage = (): Cart => {
  const savedCart = localStorage.getItem(`${getStorageKey(ShoppingCart)}`);
  return savedCart
    ? JSON.parse(savedCart)
    : { id: "", createdOn: new Date(), customerOrder: "", items: [] };
};
export const formatDate2 = (d: Date) =>
  d.toLocaleDateString("en-NZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const formatDate = (dateString: string, withTime = false): string => {
  if (!dateString) return "";
  if (dateString.length <= 8) return dateString; // likely already formatted 01/01/01 acceptable..

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";

  return withTime
    ? date.toLocaleString("en-NZ", {
        timeZone: "UTC", // ✅ force UTC formatting
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : date.toLocaleDateString("en-NZ", {
        timeZone: "UTC", // ✅ force UTC formatting
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
};

export const showPrice = (): boolean => {
  const u: JwtAuthData | null = getCurrentUser();
  return u !== null;
};
export const formatPercent = (
  value: number | null | undefined,
  defaultValue = 0,
  calculate = true,
): string => {
  const num = typeof value === "number" ? value : defaultValue;
  const percent = calculate ? num * 100 : num;

  return (
    percent.toLocaleString("en-NZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }) + "%"
  );
};

/**
 * Format number as string
 * For example,
 * 100 => 100.00
 * 1000.0 => 1000.00
 * @param num
 * @param defaultValue
 * @param decimalPlaces
 * @returns
 */
export const formatNumberAsString = (
  num: number | string | null | undefined,
  defaultValue = 0,
  decimalPlaces = 2,
): string => {
  const value = typeof num === "number" ? num : defaultValue;
  return Number(value).toFixed(decimalPlaces);
};
export const numericRegex = /^-?\d*\.?\d*$/;
// export const safeNumber = (
//   num: number | string | null | undefined,
//   defaultValue = 0,
//   decimalPlaces = 2,
// ): number => {
//   const value = typeof num === "number" ? num : defaultValue;
//   return Number(value.toFixed(decimalPlaces));
// };
export const safeNumber = (
  num: number | string | null | undefined,
  defaultValue = 0,
  decimalPlaces = 2,
): number => {
  const parsed =
    typeof num === "number"
      ? num
      : typeof num === "string"
        ? Number(num)
        : defaultValue;

  if (!Number.isFinite(parsed)) return defaultValue;

  const factor = Math.pow(10, decimalPlaces);
  return Math.round(parsed * factor) / factor;
};

/**
 * Format number as string but in currency format.
 * For example,
 * 100 => 100.00
 * 1000.0 => 1,000.00
 * @param num
 * @param defaultValue
 * @param decimalPlaces
 * @returns
 */
export const formatNumberAsCurrencyString = (
  num: number | null | undefined,
  defaultValue = 0,
  decimalPlaces = 2,
): string => {
  const value = typeof num === "number" ? num : defaultValue;
  return value.toLocaleString("en-NZ", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

export const formatNZD = (
  value: number | null | undefined,
  defaultValue = 0,
): string => {
  const num = typeof value === "number" ? value : defaultValue;

  return num.toLocaleString("en-NZ", {
    style: "currency",
    currency: "NZD",
    minimumFractionDigits: 2,
  });

  // if (value === undefined) return "0";
  // if (typeof value !== "number") return String(defaultValue) || "—";
  // return value.toLocaleString("en-NZ", {
  //   style: "currency",
  //   currency: "NZD",
  //   minimumFractionDigits: 2,
  // });
};
export const asPriceHtml = (amount: number): string => {
  if (!amount) return "<span>0<sup>00</sup></span>";

  const amt: string = amount.toFixed(2).toString();

  return `<span>${amt.substring(0, amt.indexOf("."))}<sup>${amt.substring(
    amt.indexOf(".") + 1,
  )}</sup></span>`;
};
export const convertToCSV = (data: any) => {
  const array = [Object.keys(data[0])].concat(data);

  return array
    .map((it) => {
      return Object.values(it).toString();
    })
    .join("\n");
};
export const findItemByCode = (items: Cart["items"], code: string) =>
  items.find((i) => i.productCode === code);
export const confirmDelete = () => {
  if (
    window.confirm(
      "Are you sure you want to delete this item? This action cannot be undone.",
    )
  ) {
    return true;
  }
  return false;
};
export const confirmReset = () => {
  if (
    window.confirm(
      "Are you sure you want to reset this order? Doing so will delete all line items and this action cannot be undone.",
    )
  ) {
    return true;
  }
  return false;
};
export const sanitizeText = (input: string) => {
  if (!input) return "";

  return input
    .replace(/&/g, "&amp;") // escape &
    .replace(/</g, "&lt;") // escape <
    .replace(/>/g, "&gt;") // escape >
    .replace(/"/g, "&quot;") // escape "
    .replace(/'/g, "&#39;") // escape '
    .replace(/\r?\n/g, "<br />"); // convert line breaks to <br />
};
export const escapeNewLine = (input: string) => {
  return input.replace(/\r?\n/g, "<br />");
};

export const printPage = (html: string) => {
  const printWindow = window.open("", "_blank", "width=794,height=1122");
  if (!printWindow) return;

  const doc = printWindow.document;

  // Clone styles
  const styles = Array.from(
    document.querySelectorAll("style, link[rel='stylesheet']"),
  )
    .map((node) => node.outerHTML)
    .join("\n");

  const fullHtml = `
    <html>
      <head>
        <title>Print Preview</title>
       
        <style>     
          html, body {
            zoom: 1 !important; 
          }  
          @media print {
            * { font-family: Helvetica, Verdana, "Segoe UI", Sans-Serif!important; }
   
            :root { 
              --mantine-line-height: 1.1 !important; 
              --mantine-font-size-sm: 10pt !important; 
              --mantine-font-size-md: 11pt !important; 
              --mantine-font-size-lg: 13pt !important;

              --mantine-spacing-xs: 2px !important;
              --mantine-spacing-sm: 4px !important;
              --mantine-spacing-md: 6px !important;
              --mantine-spacing-lg: 8px !important;
            }            
            body { zoom: 1 !important; line-height: 1.1!important; margin: 0; font-size: 10pt!important; }
            .print-container {width: 100% !important; max-width: 210mm !important; margin: 2px 10px !important; min-height: 95vh !important;}
            .print-padBottom { margin-bottom: 20px !important;  }
            .mantine-Table-root,
            .mantine-Table-root table,
            table {
              width: 100% !important;
              max-width: 100% !important;
            }   
            .print-title {
              font-size: 16pt !important;
            }
            .print-subTitle {
              font-size: 14pt !important;
            }
            .no-border,
            .mantine-Table-td {
              border-bottom: none !important;
            }
            
            tr { display: table-row!important; }
            tr td, tr th, td, th { padding: 1px 4px !important; }
            tr.nowrap, td.nowrap, th.nowrap { white-space: nowrap !important; }
            .alignr { text-align: right !important; }
            .money { text-align: right; white-space: nowrap !important;  }
            .money-focal { font-weight: bold; }
            .page-break { page-break-before: always; }
            .print-red { color: red; }
            tr.money-focal,
            td.money-focal  { border-top: 1px dotted red !important; border-bottom: 2px solid red !important; font-weight: bold !important; }
            /* Remove Mantine’s default spacing */
            [class*="mantine-"] { box-shadow: none !important; border-radius: 0 !important; }             
            /* Force all text to compact mode */
            .mantine-Card-root,
            .mantine-Stack-root { 
              padding-right: 4px !important;
              display: block !important;
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100% !important;
              margin: 0 !important;
            }
            .mantine-Text-root {padding-right: 4px !important; line-height: var(--mantine-line-height) !important; }
            td:first-child,
            th:first-child {
              white-space: nowrap !important;
            }

            /* Remove Grid spacing completely */
            .mantine-Grid-root,
            .mantine-Grid-col { padding-right: 4px !important; gap: 0 !important; padding: 0 !important; margin: 0 !important; }
            /* Fix Mantine Table (remove weird spacing + enforce full width) */
            /* Tighten table cells */
            table.details-table tr td { border-bottom: 1px solid #ccc !important; padding: 6px !important; }
            table.mantine-Table-root th,
            table.mantine-Table-root td, table td {table-layout: auto !important; line-height: 1.1 !important; }
            /* Allow wrapping for description column */
            td.description,
            th.description {white-space: normal !important;word-break: break-word !important;width: 60% !important; max-width: 70% !important;}
            /* Hide UI-only elements */
            .mantine-Button-root,
            .mantine-ActionIcon-root,
            .mantine-NavLink-root,
            .mantine-Pagination-root {display: none !important;}
            td.disclaimer {
              font-style: italic;
              font-weight: 500;
              font-size:11pt!important;
              color: red;
            }
          }
        </style> 
        ${styles}
      </head>
      <body>
        ${html}
         <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          };
        </script>
      </body>
    </html>
  `;

  doc.open();
  doc.write(fullHtml);
  doc.close();
};
/**
 * Retrieves the GST rate from environment or defaults to 15%.
 */
const getGstRate = (): number => {
  const gst = Number(process.env.REACT_APP_GST) || 15; // default to 15 %
  return isNaN(gst) ? 15 : gst;
};
export const getGstRateAsDecimal = (): number => {
  return getGstRate() / 100;
};
export const getGstAmount = (grossAmount: number): number => {
  const gst = Math.round(grossAmount * getGstRateAsDecimal() * 100) / 100;
  return safeNumber(gst, 0, 2); // round down to 2dp
};
/**
 * GST from Gross (GST-inclusive total)
 * Extracts the GST portion from a total that already includes GST:
 * @param totalAmount - The total amount including GST.
 * @param gstRate - The GST rate as a percentage (e.g. 15 for 15%).
 * @returns The GST portion of the total.
 */
export const calculateGSTFromGross = (
  totalAmount: number,
  gstRate?: number,
): number => {
  const gst = typeof gstRate === "number" ? gstRate : getGstRate();
  return parseFloat(((totalAmount * gst) / (100 + gst)).toFixed(2));
};

/**
 * GST from Net (GST-exclusive base)
 * Calculates the GST amount to be added to a net amount:
 * @param netAmount
 * @param gstRate
 * @returns
 */
export const calculateGSTFromNet = (
  netAmount: number,
  gstRate?: number,
): number => {
  const gst = typeof gstRate === "number" ? gstRate : getGstRate();
  return parseFloat(((netAmount * gst) / 100).toFixed(2));
};

export const getOrderStageLabel = (value: number): string => {
  return OrderStage[value] ?? "UNKNOWN";
};
